import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

/** Job data. One request for the list, one for a job's four tabs. */

export interface Job {
    readonly id: number;
    readonly jobNumber: number;
    readonly jobCode: string | null;
    readonly status: string;
    readonly jobAddress: string | null;
    readonly jobSuburb: string | null;
    readonly jobPostcode: string | null;
    readonly jobState: string | null;
    readonly clientId: number | null;
    readonly clientName: string | null;
    readonly contactName: string | null;
    readonly contactPhone: string | null;
    readonly contactEmail: string | null;
    readonly budgetCost: number | null;
    readonly costMaterials: number | null;
    readonly jobRegisterDate: string | null;
    readonly createdAt: string;
}

export interface ProfitLoss {
    readonly jobId: number;
    readonly budgetCost: number | null;
    readonly costMaterials: number | null;
    readonly totalHours: number;
    readonly costLabour: number;
    readonly totalCost: number;
    readonly profitLoss: number | null;
    readonly labourLines: readonly LabourLine[];
    readonly materialLines: readonly MaterialLine[];
}

export interface LabourLine {
    readonly id: number;
    readonly workDate: string;
    readonly staffName: string | null;
    readonly ratePerHour: number | null;
    readonly hours: number | null;
    readonly costLabour: number | null;
}

export interface MaterialLine {
    readonly id: number;
    readonly createdAt: string;
    readonly description: string;
    readonly cost: number;
    readonly addedBy: string | null;
}

export interface SwmsTask {
    readonly id: number;
    readonly task: string;
    readonly hazardsRisks: string | null;
    readonly controlMeasures: string | null;
    /** 1 Major, 2 Moderate, 3 Minor. */
    readonly riskLevel: number | null;
    /** Non-null and different means the two legacy risk scales disagree. */
    readonly legacyRiskRanking: number | null;
}

export interface SwmsSignoff {
    readonly id: number;
    readonly jobId: number;
    readonly staffId: number | null;
    readonly staffName: string;
    readonly personResponsible: string | null;
    readonly signedAt: string;
    readonly signaturePath: string | null;
    readonly signatureUrl: string | null;
    readonly pdfUrl: string | null;
}

export interface JobDetail {
    readonly job: Job;
    readonly profitLoss: ProfitLoss;
    readonly swmsTaskIds: readonly number[];
    readonly signoffs: readonly SwmsSignoff[];
}

/** The SWMS library and the client list do not change mid-edit. */
let referenceCache: { tasks: readonly SwmsTask[]; clients: readonly { id: number; companyName: string }[] } | null =
    null;

export function useJobReference(): {
    tasks: readonly SwmsTask[];
    clients: readonly { id: number; companyName: string }[];
} {
    const [reference, setReference] = useState(referenceCache ?? { tasks: [], clients: [] });

    useEffect(() => {
        if (referenceCache !== null) {
            return;
        }
        let cancelled = false;
        Promise.all([
            apiFetch<{ tasks: readonly SwmsTask[] }>('/jobs/swms-tasks'),
            apiFetch<{ clients: readonly { id: number; companyName: string }[] }>('/clients?includeInactive=1'),
        ])
            .then(([t, c]) => {
                referenceCache = { tasks: t.tasks, clients: c.clients };
                if (!cancelled) {
                    setReference(referenceCache);
                }
            })
            .catch(() => {
                /* The form still works; the pickers are just empty. */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return reference;
}

export function useJobList(
    search: string,
    status: string,
): { jobs: readonly Job[]; loading: boolean; error: string | null; reload: () => void } {
    const [jobs, setJobs] = useState<readonly Job[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const timer = setTimeout(
            () => {
                const params = new URLSearchParams({ status });
                if (search.trim() !== '') {
                    params.set('search', search.trim());
                }
                apiFetch<{ jobs: readonly Job[] }>(`/jobs?${params.toString()}`)
                    .then((r) => {
                        if (!cancelled) {
                            setJobs(r.jobs);
                            setError(null);
                        }
                    })
                    .catch((err: unknown) => {
                        if (!cancelled) {
                            setError(err instanceof Error ? err.message : 'Could not load jobs');
                        }
                    })
                    .finally(() => {
                        if (!cancelled) {
                            setLoading(false);
                        }
                    });
            },
            search.trim() === '' ? 0 : 250,
        );
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [search, status, nonce]);

    const reload = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);
    return { jobs, loading, error, reload };
}

export function useJobDetail(id: number | null): {
    detail: JobDetail | null;
    loading: boolean;
    reload: () => void;
} {
    const [detail, setDetail] = useState<JobDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [nonce, setNonce] = useState(0);

    useEffect(() => {
        if (id === null) {
            setDetail(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        apiFetch<JobDetail>(`/jobs/${String(id)}`)
            .then((r) => {
                if (!cancelled) {
                    setDetail(r);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDetail(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [id, nonce]);

    const reload = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);
    return { detail, loading, reload };
}
